// Visual picker for coldroom cells. Replaces the free-text grid input on
// pallet intake — operators can no longer type into an occupied cell.
//
// Cells are rendered as a grid sized to the coldroom's configured rows × cols.
// Empty cells are clickable (cyan when selected, gray when free), occupied
// cells are dimmed and disabled with a tooltip showing the occupant's pallet
// id. Hovering a tile in a busy room is fine — there is no fetch per tile.
//
// THE OVERFLOW (mig 20261021100001, `allowOverflow`). This component's last
// line used to be "No available cells for this pallet." — a statement, with
// nowhere to go from it. The floor's answer was to put the pallet in the aisle
// and not record it, which is how stock ages invisibly. With allowOverflow the
// grid gains a sibling choice: the room's OVERFLOW area, in the room and on no
// cell. It is offered whether or not the grid is full — a blocked aisle or an
// unreachable cell is as real as a full room — but it is never the default and
// never pre-selected, and the tile carries a running count so a floor quietly
// drifting off its own map is visible at the moment of placement rather than
// on next week's register.

import { useMemo } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { supabase } from '@/lib/supabase'
import QueryStateGate from '@/components/QueryStateGate'
import CellOverflowTile from '@/components/CellOverflowTile'
import TableScroll from '@/components/TableScroll'
import { OVERFLOW_LABEL } from '@/lib/coldroomPlacement'
import { summariseRoomOccupancy, countAvailableCells, describeCell } from '@/lib/coldroomOccupancy'

interface CellPickerProps {
  coldroomId: string | null
  value:      string                // selected cell key, e.g. 'A3' (uppercase)
  onChange:   (cell: string) => void
  /**
   * What's being placed. Default 'pallet'.
   * - 'pallet': cell is unavailable if it holds a pallet OR any bins
   * - 'bin':    cell is unavailable if it holds a pallet OR is full of bins (>= stack height)
   */
  mode?:      'pallet' | 'bin'
  excludePalletId?: string          // hide this pallet's own cell from "occupied"
  excludeBinId?:    string          // hide this bin's own cell from "occupied"
  /**
   * Offer the room's OVERFLOW area as a destination beside the grid. Opt-in,
   * because not every caller can write it: a surface that has no way to set
   * coldroom_overflow must not show a choice it cannot honour.
   */
  allowOverflow?:   boolean
  /** The overflow is the current pick. Mutually exclusive with `value`. */
  overflowPicked?:  boolean
  /** Called when the operator picks the overflow; the caller clears `value`. */
  onPickOverflow?:  () => void
  /** What the picker sits on — the pinned row letters and column numbers need
   *  that exact colour. 'shell' for a dialog or the page, 'card' inside a card. */
  surface?:         'shell' | 'card'
}

interface RoomMeta {
  rows:             number
  cols:             number
  code:             string
  bin_stack_height: number
}

interface OccupantPallet {
  pallet_id:         string
  coldroom_grid:     string | null
  coldroom_overflow: boolean | null
}

interface OccupantBin {
  bin_id:            string
  coldroom_grid:     string | null
  coldroom_overflow: boolean | null
}

export default function CellPicker({
  coldroomId, value, onChange,
  mode = 'pallet',
  excludePalletId, excludeBinId,
  allowOverflow = false, overflowPicked = false, onPickOverflow,
  surface = 'shell',
}: CellPickerProps) {
  // Room dimensions + stacking — drives the grid size + bin stacking rules.
  const roomQ = useQuery({
    queryKey: ['coldroom-meta', coldroomId],
    enabled: !!coldroomId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('coldrooms')
        .select('rows, cols, code, bin_stack_height')
        .eq('coldroom_id', coldroomId!)
        .maybeSingle()
      if (error) throw error
      return data as RoomMeta | null
    },
  })
  const room = roomQ.data

  // Everything the room is holding — both pallets and bins. Two queries kept
  // separate so cache invalidation can target one without nuking the other.
  //
  // ⚠️ The `.not('coldroom_grid', 'is', null)` these two carried is gone. It was
  // right when a cell was the only place to be, and became a blind spot the
  // moment the overflow existed: the rows it filtered out are EXACTLY the
  // overflow rows, so the picker would have offered an overflow tile that could
  // never count anything. The cell-state pass below skips the cell-less rows
  // itself, so the grid half is unchanged.
  const { data: palletOccupants = [] } = useQuery({
    queryKey: ['coldroom-occupied', coldroomId],
    enabled: !!coldroomId,
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pallets')
        .select('pallet_id, coldroom_grid, coldroom_overflow')
        .eq('status', 'IN_COLDROOM')
        .eq('coldroom_id', coldroomId!)
        .is('deleted_at', null)
      if (error) throw error
      return (data ?? []) as OccupantPallet[]
    },
  })

  const { data: binOccupants = [] } = useQuery({
    queryKey: ['coldroom-bin-occupied', coldroomId],
    enabled: !!coldroomId,
    refetchInterval: POLL.live,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bins')
        .select('bin_id, coldroom_grid, coldroom_overflow')
        .eq('status', 'IN_COLDROOM')
        .eq('coldroom_id', coldroomId!)
        .is('deleted_at', null)
      if (error) throw error
      return (data ?? []) as OccupantBin[]
    },
  })

  // One walk over both reads → the per-cell map AND the overflow count, so a
  // tile saying "free" and the overflow counter can never be reading different
  // data about the same room. The arithmetic lives in src/lib/coldroomOccupancy.ts
  // where it is unit-tested; this only decides what to show.
  const occupancy = useMemo(
    () => summariseRoomOccupancy(palletOccupants, binOccupants, { excludePalletId, excludeBinId }),
    [palletOccupants, binOccupants, excludePalletId, excludeBinId],
  )
  const cellState     = occupancy.cells
  const overflowCount = occupancy.overflowCount

  const stackLimit = room?.bin_stack_height ?? 1

  if (!coldroomId) {
    return (
      <p className="text-xs text-fg-muted italic px-1">
        Pick a coldroom above to see available cells.
      </p>
    )
  }
  // Gate, not a bare spinner: `if (!room)` spun forever on error/paused (idle-wake audit UI-3 shape).
  if (!room) {
    return (
      <div className="w-full max-w-sm">
        <QueryStateGate query={roomQ} noun="this room's layout"
          empty={<p className="text-xs text-fg-muted italic px-1">Room layout not found.</p>} />
      </div>
    )
  }

  const rowLetters = Array.from({ length: room.rows }, (_, i) => String.fromCharCode(65 + i))
  const colNumbers = Array.from({ length: room.cols }, (_, i) => i + 1)

  // "Available for this mode" — the same predicate the tiles below use, so the
  // header count and the tappable tiles cannot disagree.
  const available  = countAvailableCells(occupancy, room.rows, room.cols, mode, stackLimit)
  const totalCells = room.rows * room.cols

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[10px] text-fg-muted">
        <span>
          {room.code} layout · {room.rows} × {room.cols}
          {mode === 'bin' && stackLimit > 1 && <> · stack {stackLimit}</>}
        </span>
        <span>
          <span className="text-emerald-400">{available} free</span>
          <span className="mx-1.5 opacity-50">·</span>
          <span className="text-fg-muted">{totalCells - available} unavailable</span>
        </span>
      </div>

      {/* Row letters and column numbers stay put while the grid pans: past
          column 8 on a phone nobody could tell which cell they were on
          (PM-CST-02, PB-11). Cell PADDING, not border-spacing — the gaps a
          spacing leaves around a pinned cell show the panned cells through. */}
      <TableScroll surface={surface} maxHeight="max-h-[55vh]">
        <table className="border-collapse [&_td]:p-[1.5px] [&_th]:p-[1.5px]">
          <thead>
            <tr>
              <th></th>
              {colNumbers.map(c => (
                <th key={c} className="text-[9px] text-fg-muted font-semibold tracking-wider text-center min-w-[28px]">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowLetters.map(row => (
              <tr key={row}>
                <td className="text-[9px] text-fg-muted font-semibold tracking-wider !pr-1.5 text-right">
                  {row}
                </td>
                {colNumbers.map(col => {
                  const cellKey = `${row}${col}`
                  const state   = cellState.get(cellKey)
                  const hasPallet = !!state?.palletId
                  const binCount  = state?.binCount ?? 0
                  // The whole four-way decision, in one tested place.
                  const { unavailable, title, extra } = describeCell(cellKey, state, mode, stackLimit)
                  const selected = value === cellKey

                  return (
                    <td key={col}>
                      <button
                        type="button"
                        onClick={() => !unavailable && onChange(cellKey)}
                        disabled={unavailable}
                        title={title}
                        className={clsx(
                          'w-7 h-7 coarse:w-11 coarse:h-11 coarse:text-xs rounded border text-[9px] font-mono font-semibold tabular-nums transition-colors flex items-center justify-center',
                          unavailable
                            ? hasPallet
                              ? 'bg-indigo-900/60 border-indigo-700 text-indigo-300 cursor-not-allowed'
                              : 'surface hairline-strong text-fg-muted cursor-not-allowed'
                            : selected
                              ? 'bg-cyan-600 border-cyan-400 text-white'
                              : binCount > 0
                                ? 'bg-amber-900/40 border-amber-700/60 text-amber-200 hover:bg-amber-900/60'
                                : 'surface-raised border-line-strong text-fg-secondary hover:bg-raised-hover hover:border-gray-500',
                        )}>
                        {/* A free cell is blank: its column number sat where a
                            part-stack prints "2/4", so a free cell in column 7
                            read as "7 bins" (PM-CST-10). The pinned header
                            names the column. */}
                        {extra ?? ''}
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>

      {/* Legend */}
      <div className="flex items-center gap-2 text-[9px] text-fg-muted flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded surface-raised border border-line-strong" /> Free
        </span>
        {mode === 'bin' && (
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-amber-900/40 border border-amber-700/60" /> Has bins (stackable)
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-indigo-900/60 border border-indigo-700" /> Pallet
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded surface border hairline-strong" /> Full / blocked
        </span>
      </div>

      {/* A sibling of the grid, not a cell on it — see CellOverflowTile. */}
      {allowOverflow && onPickOverflow && (
        <CellOverflowTile picked={overflowPicked} count={overflowCount} onPick={onPickOverflow} />
      )}

      {value && !overflowPicked && (
        <p className="text-xs text-cyan-300">
          Selected: <span className="font-mono font-bold">{value}</span>
        </p>
      )}
      {overflowPicked && (
        <p className="text-xs text-amber-300">
          Selected: <span className="font-bold">{OVERFLOW_LABEL}</span> — no cell will be recorded.
        </p>
      )}
      {/* This line used to end the component with nowhere to go from it. It
          still says the true thing; it now says what to do about it. */}
      {available === 0 && (
        <p className="text-xs text-amber-400">
          No available cells for this {mode}.
          {allowOverflow && onPickOverflow && ' Put it in the overflow above so it is still on the register.'}
        </p>
      )}
    </div>
  )
}
